import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { CART_LOAD_SUCCESS } from "~/features/cart/cart/redux/actions/actionTypes";
import { buildAlternateRowId } from "~/features/cart/cartV2/utils/buildAlternateRowId";
import type { AlternateRowViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";

export interface StoredAlternateRow {
  anchorProductGroupNumber: number | null;
  productCode: string;
  designation: string;
  order: number;
}

export interface CartAlternatesState {
  rowsByTableId: Record<string, StoredAlternateRow[]>;
  nextOrderByTableId: Record<string, number>;
}

const initialState: CartAlternatesState = {
  rowsByTableId: {},
  nextOrderByTableId: {},
};

export interface InsertAlternateRowPayload {
  tableId: string;
  anchorProductGroupNumber: number | null;
  productCode: string;
  designation: string;
}

export interface CloseAlternateRowPayload {
  tableId: string;
  rowId: string;
  productGroupNumbers: number[];
}

export interface NormalizeTableRowsPayload {
  tableId: string;
  productGroupNumbers: number[];
}

function buildStoredRowIdentity(anchorProductGroupNumber: number | null, productCode: string): string {
  const anchorKey = anchorProductGroupNumber === null ? "root" : String(anchorProductGroupNumber);
  return `${anchorKey}:${productCode}`;
}

function dedupeStoredRows(storedRows: StoredAlternateRow[]): StoredAlternateRow[] {
  const rowsByIdentity = new Map<string, StoredAlternateRow>();

  storedRows.forEach((storedRow) => {
    const rowIdentity = buildStoredRowIdentity(storedRow.anchorProductGroupNumber, storedRow.productCode);
    const existingRow = rowsByIdentity.get(rowIdentity);

    if (existingRow && existingRow.order <= storedRow.order) {
      return;
    }

    rowsByIdentity.set(rowIdentity, storedRow);
  });

  return Array.from(rowsByIdentity.values()).sort((left, right) => left.order - right.order);
}

export function normalizeStoredRows(
  storedRows: StoredAlternateRow[],
  productGroupNumbers: number[],
): StoredAlternateRow[] {
  const validAnchorIds = new Set(productGroupNumbers);

  if (productGroupNumbers.length === 0) {
    return dedupeStoredRows(storedRows);
  }

  return dedupeStoredRows(
    storedRows.filter(
      (storedRow) =>
        storedRow.anchorProductGroupNumber === null || validAnchorIds.has(storedRow.anchorProductGroupNumber),
    ),
  );
}

function normalizeAnchorProductGroupNumber(
  anchorProductGroupNumber: number | null,
  validAnchorIds: Set<number>,
  preserveStoredAnchors: boolean,
): number | null | undefined {
  if (preserveStoredAnchors) {
    return anchorProductGroupNumber;
  }

  if (anchorProductGroupNumber === null || validAnchorIds.has(anchorProductGroupNumber)) {
    return anchorProductGroupNumber;
  }

  return undefined;
}

export function buildAlternateRows(args: {
  tableId: string;
  storedRows: StoredAlternateRow[];
  productGroupNumbers: number[];
}): AlternateRowViewModel[] {
  const { tableId, storedRows, productGroupNumbers } = args;
  const validAnchorIds = new Set(productGroupNumbers);
  const preserveStoredAnchors = productGroupNumbers.length === 0;
  const rowsByIdentity = new Map<string, AlternateRowViewModel & { order: number }>();

  storedRows.forEach((storedRow) => {
    const normalizedAnchorProductGroupNumber = normalizeAnchorProductGroupNumber(
      storedRow.anchorProductGroupNumber,
      validAnchorIds,
      preserveStoredAnchors,
    );

    if (normalizedAnchorProductGroupNumber === undefined) {
      return;
    }

    const rowId = buildAlternateRowId({
      tableId,
      anchorProductGroupNumber: normalizedAnchorProductGroupNumber,
      productCode: storedRow.productCode,
    });
    const existingRow = rowsByIdentity.get(rowId);

    if (existingRow && existingRow.order <= storedRow.order) {
      return;
    }

    rowsByIdentity.set(rowId, {
      kind: "alternate",
      rowId,
      tableId,
      anchorProductGroupNumber: normalizedAnchorProductGroupNumber,
      productCode: storedRow.productCode,
      designation: storedRow.designation,
      order: storedRow.order,
    });
  });

  return Array.from(rowsByIdentity.values())
    .sort((left, right) => left.order - right.order)
    .map(({ order: _order, ...row }) => row);
}

const cartAlternatesSlice = createSlice({
  name: "cartAlternates",
  initialState,
  reducers: {
    insertAlternateRow(state, action: PayloadAction<InsertAlternateRowPayload>) {
      const { tableId, anchorProductGroupNumber, productCode, designation } = action.payload;
      const currentRows = state.rowsByTableId[tableId] ?? [];

      const alreadyExists = currentRows.some(
        (row) => row.anchorProductGroupNumber === anchorProductGroupNumber && row.productCode === productCode,
      );

      if (alreadyExists) {
        return;
      }

      const nextOrder = state.nextOrderByTableId[tableId] ?? 0;
      state.rowsByTableId[tableId] = [
        ...currentRows,
        { anchorProductGroupNumber, productCode, designation, order: nextOrder },
      ];
      state.nextOrderByTableId[tableId] = nextOrder + 1;
    },

    closeAlternateRow(state, action: PayloadAction<CloseAlternateRowPayload>) {
      const { tableId, rowId, productGroupNumbers } = action.payload;
      const currentRows = state.rowsByTableId[tableId] ?? [];

      const builtRows = buildAlternateRows({ tableId, storedRows: currentRows, productGroupNumbers });
      const targetRow = builtRows.find((row) => row.rowId === rowId);

      if (!targetRow) {
        return;
      }

      const validAnchorIds = new Set(productGroupNumbers);
      state.rowsByTableId[tableId] = currentRows.filter((row) => {
        const normalizedAnchorId = normalizeAnchorProductGroupNumber(
          row.anchorProductGroupNumber,
          validAnchorIds,
          productGroupNumbers.length === 0,
        );
        return !(
          normalizedAnchorId === targetRow.anchorProductGroupNumber && row.productCode === targetRow.productCode
        );
      });
    },

    normalizeTableRows(state, action: PayloadAction<NormalizeTableRowsPayload>) {
      const { tableId, productGroupNumbers } = action.payload;
      const currentRows = state.rowsByTableId[tableId] ?? [];
      const nextRows = normalizeStoredRows(currentRows, productGroupNumbers);
      state.rowsByTableId[tableId] = nextRows;
    },

    removeTable(state, action: PayloadAction<string>) {
      delete state.rowsByTableId[action.payload];
      delete state.nextOrderByTableId[action.payload];
    },

    clearAllAlternates(state) {
      state.rowsByTableId = {};
      state.nextOrderByTableId = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(CART_LOAD_SUCCESS, (state) => {
      state.rowsByTableId = {};
      state.nextOrderByTableId = {};
    });
  },
});

export const { insertAlternateRow, closeAlternateRow, normalizeTableRows, removeTable, clearAllAlternates } =
  cartAlternatesSlice.actions;

export default cartAlternatesSlice.reducer;
